import React, { useEffect, useState } from 'react';
import { Camera, History as HistoryIcon } from 'lucide-react';
import CreateRelease from './CreateRelease';
import History from './History';
import ReleaseDetail from './ReleaseDetail';
import { initTelegram, isInsideTelegram } from './telegram';
import { useLang } from '../i18n';
import type { ServerConfig } from '@shared/types';

type Tab = 'create' | 'history';

const readUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const tab = (params.get('tab') as Tab | null) ?? 'create';
  const token = params.get('token');
  return {
    tab: tab === 'history' || tab === 'create' ? tab : ('create' as Tab),
    token,
  };
};

interface AdminAppProps {
  config: ServerConfig;
}

const AdminApp: React.FC<AdminAppProps> = ({ config }) => {
  const { t } = useLang();
  const [tab, setTab] = useState<Tab>(readUrl().tab);
  const [detailToken, setDetailToken] = useState<string | null>(readUrl().token);

  useEffect(() => {
    initTelegram();
  }, []);

  if (!isInsideTelegram() && !config.devBypassTelegramAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-gray-50">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-xl font-bold serif text-gray-900">{t('admin.openInTelegram.title')}</h1>
          <p className="text-sm text-gray-500">{t('admin.openInTelegram.body')}</p>
        </div>
      </div>
    );
  }

  if (detailToken) {
    return <ReleaseDetail token={detailToken} onClose={() => setDetailToken(null)} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto flex">
          <TabButton active={tab === 'create'} onClick={() => setTab('create')}>
            <Camera size={15} /> {t('admin.tabs.create')}
          </TabButton>
          <TabButton active={tab === 'history'} onClick={() => setTab('history')}>
            <HistoryIcon size={15} /> {t('admin.tabs.history')}
          </TabButton>
        </div>
      </nav>

      {tab === 'create' ? (
        <CreateRelease aiEnabled={config.aiEnabled} defaults={config.defaults} />
      ) : (
        <History onOpen={setDetailToken} />
      )}
    </div>
  );
};

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-3.5 text-sm font-semibold flex items-center justify-center gap-1.5 transition border-b-2 ${
      active
        ? 'border-indigo-600 text-indigo-600'
        : 'border-transparent text-gray-400 hover:text-gray-600'
    }`}
  >
    {children}
  </button>
);

export default AdminApp;
